import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Heading,
  Loader,
  ScrollView,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  View,
  Icon,
  Alert,
  SelectField,
  Accordion,
  CheckboxField,
  useAuthenticator
} from "@aws-amplify/ui-react";
import "./Badanti.css";
import { Modal } from "react-bootstrap";
import { MdBlock, MdEdit, MdLock, MdLockOpen } from "react-icons/md";
import { blockUserAPI, getBadantiBozzeListAPI, unblockUserAPI } from "../../data/api";
import { getUserRoleLabel } from "../../data/interfaces";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { BsSortDown, BsSortUp } from "react-icons/bs";
import { badantiAttributes, bozzeColumns, getLabel } from "./config";
import CreateBadanteBozzaForm from "./CreateBadanteBozzaForm";
import CreateUserForm from "../users/CreateUserForm";
import BadantiTable from "./BadantiTable";
import BadanteFilters from "./BadanteFilters";


const Badanti = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();
  const [badanti, setBadanti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [emptyState, setEmptyState] = useState(false);
  const [isLastPage, setIsLastPage] = useState(false);
  const pageSize = 10;
  const selectedColumns = bozzeColumns;

  const [order, setOrder] = useState(undefined)
  const [sortKey, setSortKey] = useState(undefined)
  const updateOrder = (key) => {
    if(key === sortKey) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setOrder('asc');
    }
    setBadanti([]);
  }  
  
  useEffect(() => {
    getBadantiList();
  }, [sortKey, order]);

  const getBadantiList = async () => {
    setLoading(true)
    setEmptyState(false);
    setError(false);
    try {
      const badantiList = await getBadantiBozzeListAPI(badanti.length, pageSize, sortKey, order, filters);
      setIsLastPage(badantiList.last);
      setBadanti([...badanti, ...badantiList.badanti])
      setLoading(false);
    } catch (e) {
      if(e.statusCode === 404){
        if(badanti.length === 0){
          console.log('No bozze badanti found', e);
          setEmptyState(true);
        } else {
          setIsLastPage(true);
        }
      } else {
        console.log('Could not load bozze badanti list: ', e);
        setError(true);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    getBadantiList();
  }, []);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);
  const [filters, setFilters] = useState(() => {
    const storage = localStorage.getItem('filterRicercaBadante'+user?.username);
    const filters = storage ? JSON.parse(storage) : {};
    setActiveFilters(Object.keys(filters).filter(f => filters[f].length != 0 && filters[f] !== '').length ?? 0)
    return filters;
  });;
  const setFiltersRicerca = (e) => {
    let newValue;
    if(e.target.value === "" || JSON.stringify(e.target.value) === '{}' || JSON.stringify(e.target.value) === '[]'){
      newValue = {
        ...filters
      }
      delete newValue[e.target.name];
    } else {
      newValue = {
        ...filters,
        [e.target.name]: e.target.value,
      };
    }
    setFilters(newValue);
    localStorage.setItem('filterRicercaBadanteBozza'+user?.username, JSON.stringify(newValue));
  };
  // State to manage reload key whenever the filters are applied
  const [reloadKey, setReloadKey] = useState(0);

  const handleFilterChange = (e) => {
    setFiltersRicerca(e);
  };

  const resetFilters = (e) => {
    setBadanti([]);
    setFilters({});
    localStorage.removeItem('filterRicercaBadanteBozza'+user?.username);
    setShowFilterModal(false);
    setActiveFilters(0);
    setReloadKey(reloadKey+1)
  };

  const handleFilterSubmit = () => {
    setBadanti([]);
    setShowFilterModal(false);
    setActiveFilters(Object.keys(filters).filter(f => filters[f].length != 0 && filters[f] !== '').length ?? 0)
    setReloadKey(reloadKey+1)
  };

  useEffect(() => {
    getBadantiList();
  }, [reloadKey]);
  
  const loadNextPage = async () => {
    if(!isLastPage){
      getBadantiList();
    }
  };

  const observer = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useCallback((node: HTMLDivElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadNextPage();
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, loadNextPage]);

  return (
    <View maxWidth="100%" padding="0rem" minHeight="100vh">
      <View
        backgroundColor="var(--amplify-colors-white)"
        borderRadius="6px"
        maxWidth="100%"
        padding="1rem"
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2>Bozze operatori sanitari</h2>
          <View>
            {activeFilters > 0 && <Button variation="warning" onClick={resetFilters} marginLeft = "10px">
              Rimuovi tutti i filtri
            </Button>}
            <Button 
              onClick={() => setShowFilterModal(true)} 
              variation={activeFilters > 0 ? 'primary' : undefined} marginLeft = "10px">Filtri di ricerca ({activeFilters})
            </Button>
            <Button className="creaNuovaBozza" variation="primary" marginLeft = "10px" onClick={() => navigate('/os/bozze/new')}>Crea nuova bozza</Button>
          </View>
        </div>
        <ScrollView width="100%">
          {error ? <Alert variation="error">Errore nel caricamento dei dati</Alert> : 
          emptyState ? <Alert variation="info">Nessuna bozza trovata</Alert> :
            <><BadantiTable
              columns={selectedColumns}
              badanti = {badanti}
              order={order}
              sortKey={sortKey}
              onUpdateOrder={(c) => updateOrder(c)}
              onClickBadante={(id) => navigate('/os/bozze/detail?id='+id)}
            ></BadantiTable>
            <div ref={lastItemRef} /></>
          }
          { loading && <Loader/> } 
        </ScrollView>
      </View>

      <Modal show={showFilterModal} onHide={() => {setShowFilterModal(false); handleFilterSubmit();}} size="xl">
        <Modal.Header>
          <Modal.Title>Imposta filtri di ricerca</Modal.Title>
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
            <Button variation="primary" onClick={handleFilterSubmit} style={{ marginRight: '10px' }}>
              Applica filtri
            </Button>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => { setShowFilterModal(false); }}></button>
          </div>
        </Modal.Header>
        <Modal.Body>
          <ScrollView padding="20px">
            <BadanteFilters 
              value={filters}
              savedValue={filters}
              onChange={handleFilterChange}
              filterType="bozza">
            </BadanteFilters>
          </ScrollView>
        </Modal.Body>
        <Modal.Footer>
          <Button variation="primary" onClick={handleFilterSubmit} marginLeft = "10px">
            Applica filtri
          </Button>
        </Modal.Footer>
      </Modal>
    </View>
  );
};

export default Badanti;
